import React from 'react';
import { Loader2 } from 'lucide-react';
import WriterCompileStatusBanner from '@/components/writer/WriterCompileStatusBanner';
import WriterPdfViewer from '@/components/writer/WriterPdfViewer';
import WriterVersionScrubber, {
  type WriterVersionSummary,
} from '@/components/writer/WriterVersionScrubber';
import WriterSourceExportButton from '@/components/writer/WriterSourceExportButton';
import WriterFreeMarkChip from '@/components/writer/WriterFreeMarkChip';
import WriterFreeMarkAdminToggle from '@/components/writer/WriterFreeMarkAdminToggle';

import { DocStateChip } from '@/components/writer/writerDocState';

import type { WriterDocState } from '@/hooks/useWriterDocState';

export type WriterPdfState = {
  loading: boolean;
  bytes: Uint8Array | null;
  url: string | null;
  signedUrl: string | null;
  error?: string;
  reason?: string;
};

export interface WriterPdfPaneProps {
  versions: WriterVersionSummary[];
  busyVersionId: string | null;
  docState: WriterDocState | null;
  pdf: WriterPdfState;
  onRefreshVersions: () => void;
  onRevert: (v: WriterVersionSummary) => void | Promise<void>;
  onFork: (v: WriterVersionSummary) => void | Promise<void>;
  onRetryCompile: (v: WriterVersionSummary) => void | Promise<void>;
  onReassemble: (v: WriterVersionSummary) => void | Promise<void>;
  onReload: () => void;
  /** Hides chrome the mobile status bar already renders. */
  compact?: boolean;
}

/**
 * Presentational compiled-PDF pane. Shared verbatim by the desktop/tablet
 * two-pane layout and the mobile PDF sheet so both surfaces stay in lockstep.
 */
const WriterPdfPane: React.FC<WriterPdfPaneProps> = ({
  versions,
  busyVersionId,
  docState,
  pdf,
  onRefreshVersions,
  onRevert,
  onFork,
  onRetryCompile,
  onReassemble,
  onReload,
  compact = false,
}) => {
  return (
    <div className="flex flex-col min-w-0 flex-1 min-h-0">
      <WriterVersionScrubber
        versions={versions}
        onRefresh={onRefreshVersions}
        onRevert={onRevert}
        onFork={onFork}
        onRetryCompile={onRetryCompile}
        busyVersionId={busyVersionId}
      />
      <WriterCompileStatusBanner
        latest={versions[0] ?? null}
        busy={busyVersionId === versions[0]?.id}
        onRetry={onReassemble}
      />

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 border-b border-border/60 bg-background/70">
        <div className="flex items-center gap-3 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
            Compiled PDF
          </p>
          {/* AI Act Art. 50: machine-generated content must be marked as such */}
          <span
            className="shrink-0 rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground"
            title="Parts of this document were generated with AI assistance (Google Vertex AI, EU/EEA). Verify content and citations before publication."
          >
            AI-assisted
          </span>
          <WriterFreeMarkChip
            version={
              versions.find((v) => v.compile_status === 'success') ?? versions[0] ?? null
            }
            onRecompile={onRetryCompile}
            busy={Boolean(busyVersionId)}
          />
          <WriterFreeMarkAdminToggle
            version={
              versions.find((v) => v.compile_status === 'success') ?? versions[0] ?? null
            }
            onRecompile={onRetryCompile}
            busy={Boolean(busyVersionId)}
          />

          {!compact && <DocStateChip docState={docState} />}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {pdf.signedUrl ? (
            <a
              href={pdf.signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-foreground"
            >
              Open
            </a>
          ) : null}
          {pdf.url ? (
            <a
              href={pdf.url}
              download="paperverse-writer.pdf"
              className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-foreground"
            >
              Download
            </a>
          ) : null}
          {/* Raw LaTeX of the version the shown PDF was built from. */}
          <WriterSourceExportButton
            versionId={
              versions.find((v) => v.compile_status === 'success')?.id ?? versions[0]?.id ?? null
            }
            label={compact ? 'LaTeX' : 'LaTeX source'}
          />

        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
        {pdf.loading && !pdf.url ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <p className="text-xs uppercase tracking-widest">Loading compiled PDF…</p>
          </div>
        ) : pdf.bytes && !pdf.error ? (
          <WriterPdfViewer data={pdf.bytes} />
        ) : pdf.error ? (
          <div className="text-center max-w-sm px-6 py-16 space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Compiled PDF
            </p>
            <p className="text-sm text-muted-foreground">
              Could not load the compiled PDF: {pdf.error}
            </p>
            <div className="flex items-center justify-center gap-3 text-sm">
              {pdf.signedUrl ? (
                <a
                  href={pdf.signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  Open in new tab
                </a>
              ) : null}
              {pdf.url ? (
                <a
                  href={pdf.url}
                  download="paperverse-writer.pdf"
                  className="underline hover:text-foreground"
                >
                  Download
                </a>
              ) : null}
              <button type="button" onClick={onReload} className="underline hover:text-foreground">
                Reload
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center max-w-sm px-6 py-16">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Compiled PDF
            </p>
            <p className="text-sm text-muted-foreground">
              {versions.length === 0
                ? 'No draft yet — ask Writer for a section to create the first version.'
                : pdf.reason === 'artifact_not_ready'
                  ? "The compiled artifact isn't available yet — Tectonic may still be finalizing."
                  : pdf.reason
                    ? `Compiled PDF unavailable (${pdf.reason}).`
                    : 'Version queued for compile. Tectonic is building your PDF on our EU (europe-west4) worker; it should appear here in a few seconds.'}
            </p>
            <button
              type="button"
              onClick={onReload}
              className="mt-4 text-xs underline text-muted-foreground hover:text-foreground"
            >
              Reload
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WriterPdfPane;
